import React, { useEffect, useMemo, useRef } from 'react';

export interface MasterVuProProps {
  // Post-fader analyser (what you hear). Required for level display.
  postAnalyser: AnalyserNode | null;
  // Visuals
  width?: number; // if omitted, canvas will expand to parent width
  height?: number; // keep low for horizontal bar
  floorDb?: number; // meter floor (left edge maps to this dB)
  ceilingDb?: number; // right edge reference / limiter ceiling
  fps?: number; // render rate
  showScale?: boolean; // draw tick marks
  showDbfs?: boolean; // overlay numeric dBFS at top-right
  dbfsColor?: string; // text color for dBFS overlay
  font?: string; // canvas font, e.g. '10px Inter, system-ui, sans-serif'
  className?: string;
}

// Utility: convert dBFS value into [0..1] based on floor/ceiling
function normDb(db: number, floor: number, ceil: number) {
  if (!isFinite(db)) return 0;
  const t = (db - floor) / (ceil - floor);
  return Math.max(0, Math.min(1, t));
}

// Ballistics smoother (separate attack/release times in seconds)
function makeSmoother(attack: number, release: number) {
  let last = -160;
  return (db: number, dt: number) => {
    if (!isFinite(db)) return last;
    const diff = db - last;
    const tau = diff > 0 ? attack : release;
    const a = Math.exp(-dt / Math.max(1e-4, tau));
    last = a * last + (1 - a) * db;
    return last;
  };
}

const MasterVuPro: React.FC<MasterVuProProps> = ({
  postAnalyser,
  width,
  height = 18,
  floorDb = -60,
  ceilingDb = -0.1,
  fps = 30,
  showScale = true,
  showDbfs = true,
  dbfsColor = '#d1d5db',
  font = '10px ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial',
  className,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const lastTsRef = useRef(0);
  const peakHoldRef = useRef<{ db: number; time: number }>({ db: -160, time: 0 });
  const smoothLevel = useMemo(() => makeSmoother(0.025, 0.300), []); // ~25ms attack, 300ms release

  useEffect(() => {
    const cvs = canvasRef.current;
    const ctx = cvs?.getContext('2d');
    if (!cvs || !ctx || !postAnalyser) return;
    // initial sizing: fill parent width when width prop is undefined
    const ensureSize = () => {
      const w = (typeof width === 'number' && isFinite(width))
        ? width
        : Math.max(1, Math.floor(cvs.parentElement?.clientWidth || 320));
      if (cvs.width !== w) cvs.width = w;
      if (cvs.height !== height) cvs.height = height;
    };
    ensureSize();

    // observe parent size changes for responsive width
    let ro: ResizeObserver | null = null;
    let roRaf: number | null = null;
    if (typeof width !== 'number') {
      try {
        ro = new ResizeObserver(() => {
          // Defer canvas dimension writes to next frame to avoid
          // "ResizeObserver loop completed with undelivered notifications".
          if (roRaf) cancelAnimationFrame(roRaf);
          roRaf = requestAnimationFrame(() => {
            ensureSize();
            roRaf = null;
          });
        });
        ro.observe(cvs.parentElement || cvs);
      } catch {}
    }
  const bufPost = new Float32Array(postAnalyser.fftSize || 2048);

    const render = (ts: number) => {
      const dt = lastTsRef.current ? (ts - lastTsRef.current) / 1000 : 1 / fps;
      lastTsRef.current = ts;

      // Measure RMS and peak from post analyser
      postAnalyser.getFloatTimeDomainData(bufPost);
      let sum = 0; let peak = 0;
      for (let i = 0; i < bufPost.length; i++) {
        const v = bufPost[i]; sum += v * v; const a = Math.abs(v); if (a > peak) peak = a;
      }
      const rms = Math.sqrt(sum / bufPost.length) || 1e-9;
      const rmsDb = 20 * Math.log10(rms);
      const peakDb = 20 * Math.log10(Math.max(1e-9, peak));
      const smoothedDb = smoothLevel(rmsDb, dt);

      // Peak-hold (decays ~1 dB/s)
      const now = ts / 1000;
      if (peakDb > peakHoldRef.current.db + 0.1) {
        peakHoldRef.current = { db: peakDb, time: now };
      } else if (now - peakHoldRef.current.time > 0.05) {
        peakHoldRef.current.db = Math.max(floorDb, peakHoldRef.current.db - dt * 1.0);
      }

      // No gain reduction visual (limiter/compressor removed)

      // Draw
      const w = cvs.width; const h = cvs.height;
      ctx.clearRect(0, 0, w, h);
      // Background
      ctx.fillStyle = '#0b0b0b';
      ctx.fillRect(0, 0, w, h);

      // Colored level fill (left -> right)
  const norm = normDb(smoothedDb, floorDb, ceilingDb);
      const levelW = Math.max(1, Math.floor(norm * w));
      const grad = ctx.createLinearGradient(0, 0, w, 0);
      grad.addColorStop(0.00, '#34d399'); // green
      grad.addColorStop(0.70, '#facc15'); // yellow
      grad.addColorStop(0.92, '#f97316'); // orange
      grad.addColorStop(0.98, '#ef4444'); // red
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, levelW, h);

      // Peak hold marker
  const peakX = Math.floor(normDb(peakHoldRef.current.db, floorDb, ceilingDb) * w);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(Math.max(0, Math.min(w - 1, peakX)), 0, 2, h);

      // Ceiling marker (e.g., -0.1 dBFS)
  const ceilX = Math.floor(normDb(ceilingDb, floorDb, ceilingDb) * w);
      ctx.fillStyle = '#9ca3af'; // gray line
      ctx.fillRect(Math.max(0, Math.min(w - 1, ceilX)), 0, 1, h);

      // (no GR bar)

      // Optional scale ticks
      if (showScale) {
        ctx.fillStyle = '#3f3f46';
        const ticks = [-60, -24, -12, -6, -3, -1, 0];
        for (const t of ticks) {
          if (t < floorDb) continue;
          const x = Math.floor(normDb(t, floorDb, ceilingDb) * w);
          const th = (t === 0 || t === -6 || t === -12 || t === -24) ? h : Math.floor(h * 0.5);
          ctx.fillRect(Math.max(0, Math.min(w - 1, x)), h - th, 1, th);
        }
      }

      // Numeric dBFS readout (top-right)
      if (showDbfs) {
        ctx.font = font;
        ctx.textAlign = 'right';
        ctx.textBaseline = 'top';
        ctx.fillStyle = dbfsColor;
        const shownDb = Math.min(ceilingDb, smoothedDb);
        const label = isFinite(shownDb) ? `${shownDb.toFixed(1)} dBFS` : '-∞ dBFS';
        ctx.fillText(label, w - 4, 2);
      }

      rafRef.current = requestAnimationFrame(render);
    };
    rafRef.current = requestAnimationFrame(render);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      try {
        if (roRaf) cancelAnimationFrame(roRaf);
      } catch {}
      try { ro?.disconnect?.(); } catch {}
    };
  }, [postAnalyser, width, height, fps, floorDb, ceilingDb, showScale, smoothLevel]);

  return (
    <canvas ref={canvasRef} className={className} />
  );
};

export default MasterVuPro;
